/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch {
      // Ignore storage errors so storefront flows keep working.
    }
  }

  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch {
      // Ignore storage errors so storefront flows keep working.
    }
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    try {
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }
}

/**
 * Stores product context used to recover Shopify recommendations when the cart is empty.
 */
export class RecommendationContext {
  /** @static @constant {string} */
  static #LAST_ADDED_PRODUCT_KEY = 'lastAddedProductId';

  /**
   * Stores the last successfully added product id.
   * @param {string} productId
   */
  static setLastAddedProduct(productId) {
    if (!productId) return;
    try {
      localStorage.setItem(this.#LAST_ADDED_PRODUCT_KEY, productId);
    } catch {
      // Ignore storage errors so storefront flows keep working.
    }
  }

  /**
   * Returns the last successfully added product id.
   * @returns {string}
   */
  static getLastAddedProduct() {
    try {
      return localStorage.getItem(this.#LAST_ADDED_PRODUCT_KEY) || '';
    } catch {
      return '';
    }
  }

  /**
   * Returns the most recent product viewed in the storefront.
   * @returns {string}
   */
  static getLastViewedProduct() {
    return RecentlyViewed.getProducts()[0] || '';
  }
}
